import { DataType, FormDialogMode, useNotify, UserPermission } from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumTipoPeaje } from '../../../api'
import {
  QuoteContractModel,
  QuotesModel
} from '../../../api/Argentia/endpoints/quotes/model/quotes'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import { enumKeyToValue } from '../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useConfirmationDialog, useFormDialog } from '../../../hooks'
import { useDropdownOptions } from '../../../hooks/useDropdownOptions'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { ContractFormRaw, NewContractProduct } from '../../contracts/contracts.model'
import {
  mapQuoteContractFormDataToRequest,
  mapQuoteModelListToSelectOption,
  mapServiceResponseToQuotesProductCard
} from '../quotes.mapper'
import { QuotesProductTableModel } from '../quotes.model'
import {
  createQuoteContract,
  deleteQuoteContract,
  fetchContractQuotes,
  fetchQuotes,
  updateQuoteContract
} from '../quotes.service'
import { QuotesProductsLeftPanelDetails } from './components/quotes.products.leftPanel'
import { QuotesProductsCard } from './components/quotesProductCard/quotes.products.card'

interface ProductsQuotesPageProps {
  location?: {
    state: {
      productData?: NewContractProduct
      contractData?: Partial<ContractFormRaw> | null
      tollType?: EnumTipoPeaje
    }
  }
}

export const QuotesProductsPage: React.FC<ProductsQuotesPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'quotes'])
  const history = useHistory()
  const { info, error } = useNotify()
  const { openForm, updateFormDefinition, updateFormData, closeForm, formProps, CustomFormDialog } =
    useFormDialog()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const quotesDropdownOptions = useDropdownOptions<QuotesModel, [number, number | undefined]>(
    fetchQuotes
  )

  const productData = props.location?.state?.productData
  const contractData = props.location?.state?.contractData
  const commodity = contractData?.commodity
  const tollTypeProp = props.location?.state?.tollType
  const tollType =
    tollTypeProp && typeof tollTypeProp === 'string'
      ? (enumKeyToValue(EnumTipoPeaje, tollTypeProp) as number)
      : tollTypeProp

  if (!productData || !commodity) {
    history.goBack()
    return null
  }

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [productQuotes, setProductQuotes] = useState<QuotesProductTableModel[]>([])
  const [otherQuotes, setOtherQuotes] = useState<QuotesProductTableModel[]>([])

  const [formMode, setFormMode] = useState<FormDialogMode>(FormDialogMode.ADD)

  const [invalidDates, setInvalidDates] = useState<boolean>(false)

  // #region Handlers Starts

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleBackButtonClick = () => {
    history.goBack()
  }

  // ------------------- Quotes Product Card Handlers -----------------------------//

  const handleAddQuoteContract = async () => {
    quotesDropdownOptions.responseData?.length === 0 &&
      quotesDropdownOptions.fetchData(productData.productId, tollType)
    setFormMode(FormDialogMode.ADD)
    openForm({
      definition: buildFormDefinition(FormDialogMode.ADD),
      title: t('quotes:components.quotesProductsCard.forms.add.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: formData => onFormCloseHandler(formData, FormDialogMode.ADD)
    })
  }

  const handleEditQuoteContract = async (selectedRow: QuotesProductTableModel) => {
    setFormMode(FormDialogMode.EDIT)
    openForm({
      definition: buildFormDefinition(FormDialogMode.EDIT),
      title: t('quotes:components.quotesProductsCard.forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: selectedRow,
      onClose: formData => onFormCloseHandler(formData, FormDialogMode.EDIT)
    })
    // Edit Quote Contract Logic
  }

  const handleDeleteQuoteContract = async (selectedRow: QuotesProductTableModel) => {
    await getConfirmation(ConfirmationDialogs.DELETE_QUOTE_CONTRACT).then(() => {
      deleteContractQuote(selectedRow.quoteId, productData.contractId)
    })
    closeConfirmation()
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const validateDates = useCallback((startDate: string, endDate: string) => {
    if (!endDate) {
      setInvalidDates(false)
      return
    }
    if (moment(startDate).isAfter(moment(endDate))) {
      setInvalidDates(true)
    } else {
      setInvalidDates(false)
    }
  }, [])
  // #endregion Functions Ends

  // #region Form Definition Starts

  const onFormCloseHandler = async (
    formData: QuotesProductTableModel,
    formMode: FormDialogMode
  ) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(formDefinition?.sections!, formData)) {
        error(t('quotes:notifications.incompleteForm'))
        return
      }
      const request = mapQuoteContractFormDataToRequest(formData, productData.contractId)
      const success =
        formMode === FormDialogMode.ADD
          ? await saveContractQuote(formData.quoteId, request)
          : await updateContractQuote(formData.quoteId, productData.contractId, request)

      if (!success) {
        return // prevent closing the form on error and losing data
      }
    }
    closeForm()
    setInvalidDates(false)
  }

  const buildFormDefinition = useCallback(
    (mode: FormDialogMode): FormDialogDefinitionProps => {
      const isEditMode = mode === FormDialogMode.EDIT
      return {
        id: 'QUOTE_CONTRACT_FORM',
        sections: [
          {
            inputs: [
              {
                name: 'contractId',
                type: DataType.NUMBER,
                display: false
              },
              {
                name: 'quoteId',
                type: DataType.SELECT,
                label: t('quotes:components.quotesProductsCard.forms.inputs.quote'),
                isLoading: quotesDropdownOptions.isLoading,
                selectOptions: mapQuoteModelListToSelectOption(
                  quotesDropdownOptions.responseData,
                  productQuotes
                ),
                display: !isEditMode,
                required: true,
                inputSize: 12,
                helpText: t('quotes:components.quotesProductsCard.forms.placeholders.quoteId')
              },
              {
                name: 'code',
                label: t('quotes:components.quotesProductsCard.forms.inputs.code'),
                type: DataType.TEXT,
                readOnly: true,
                display: isEditMode
              },
              {
                name: 'description',
                label: t('quotes:components.quotesProductsCard.forms.inputs.description'),
                type: DataType.TEXT,
                readOnly: true,
                display: isEditMode
              },
              {
                name: 'signDate',
                label: t('quotes:components.quotesProductsCard.forms.inputs.signDate'),
                type: DataType.DATE,
                required: true,
                inputSize: 4
              },
              {
                name: 'startDate',
                label: t('quotes:components.quotesProductsCard.forms.inputs.startDate'),
                type: DataType.DATE,
                required: true,
                inputSize: 4,
                errorState: invalidDates,
                errorText: t(
                  'quotes:components.quotesProductsCard.forms.errors.startDateAfterEndDate'
                ),
                onChange: (value: string | number, formState: any) => {
                  const startDate = value?.toString()
                  const endDate = formState.endDate
                  validateDates(startDate, endDate)
                  updateFormData({ ...formState, startDate })
                }
              },
              {
                name: 'endDate',
                label: t('quotes:components.quotesProductsCard.forms.inputs.endDate'),
                type: DataType.DATE,
                inputSize: 4,
                errorState: invalidDates,
                errorText: t(
                  'quotes:components.quotesProductsCard.forms.errors.startDateAfterEndDate'
                ),
                onChange: (value: string | number, formState: any) => {
                  const endDate = value?.toString()
                  const startDate = formState.startDate
                  validateDates(startDate, endDate)
                  updateFormData({ ...formState, endDate })
                }
              }
            ]
          }
        ]
      }
    },
    [
      quotesDropdownOptions.isLoading,
      quotesDropdownOptions.responseData,
      productQuotes,
      invalidDates,
      validateDates
    ]
  )

  const formDefinition = useMemo(
    () => buildFormDefinition(formMode),
    [formMode, buildFormDefinition]
  )

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  const getContractQuotes = useCallback(
    async contractId => {
      setIsFetching(true)
      await fetchContractQuotes(contractId)
        .then(response => {
          if (response.success && response.data) {
            if (response.data.length > 0) {
              const { specificProductQuotes, otherQuotes } = mapServiceResponseToQuotesProductCard(
                response.data,
                productData.productId
              )
              // set quotes from productId
              setProductQuotes(specificProductQuotes)
              // set all other quotes
              setOtherQuotes(otherQuotes)
            }
          } else {
            error(t('quotes:notifications.getQuoteContracts.error'))
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    },
    [productData.productId]
  )

  const saveContractQuote = useCallback(
    (quoteId: number, request: QuoteContractModel): Promise<boolean> => {
      return createQuoteContract(quoteId, request).then(response => {
        if (response.success && response.data) {
          info(t('quotes:notifications.createQuoteContract.success'))
          getContractQuotes(productData.contractId)
          return true
        } else {
          error(t('quotes:notifications.createQuoteContract.error'))
          return false
        }
      })
    },
    [productData.contractId]
  )

  const updateContractQuote = useCallback(
    (quoteId: number, contractId: number, request: QuoteContractModel): Promise<boolean> => {
      return updateQuoteContract(quoteId, contractId, request).then(response => {
        if (response.success && response.data) {
          info(t('quotes:notifications.updateQuoteContract.success'))
          getContractQuotes(contractId)
          return true
        } else {
          error(t('quotes:notifications.updateQuoteContract.error'))
          return false
        }
      })
    },
    []
  )

  const deleteContractQuote = useCallback(async (quoteId: number, contractId: number) => {
    await deleteQuoteContract(quoteId, contractId).then(response => {
      if (response.success) {
        info(t('quotes:notifications.deleteQuoteContract.success'))
        getContractQuotes(contractId)
      } else {
        error(t('quotes:notifications.deleteQuoteContract.error'))
      }
    })
  }, [])

  useEffect(() => {
    getContractQuotes(productData.contractId)
  }, [getContractQuotes, productData.contractId])

  useEffect(() => {
    quotesDropdownOptions.responseError && error(t('quotes:notifications.getQuotes.error'))
  }, [quotesDropdownOptions.responseError])

  useEffect(() => {
    updateFormDefinition(formDefinition)
  }, [formDefinition])

  // #endregion Hooks Ends

  return (
    <Page layout={PageLayout.MULTI_COLUMN} fullWidth={true}>
      <PageLeftColumn>
        <QuotesProductsLeftPanelDetails
          productData={productData}
          contractData={contractData}
          commodity={commodity}
        />
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('quotes:components.quotesProducts.title')}>
          <Button variant="text" color="primary" size="small" onClick={handleBackButtonClick}>
            {t('quotes:actions.back')}
          </Button>
        </PageHeader>
        {/* MAIN BODY COMPONENTS */}
        <QuotesProductsCard
          isLoading={isFetching}
          data={productQuotes}
          subtitle={t('quotes:components.quotesProductsCard.productSubtitle')}
          onAddQuoteContractClick={handleAddQuoteContract}
          emptyButtonCallback={handleAddQuoteContract}
          onEditQuoteContractClick={handleEditQuoteContract}
          onDeleteQuoteContractClick={handleDeleteQuoteContract}
        />
        {otherQuotes.length > 0 && (
          <QuotesProductsCard
            isLoading={isFetching}
            data={otherQuotes}
            subtitle={t('quotes:components.quotesProductsCard.contractSubtitle')}
            userPermissions={[UserPermission.READ_ONLY]}
          />
        )}
        <CustomFormDialog {...formProps} />
      </PageCenterColumn>
    </Page>
  )
}
